// src/pages/Login.jsx
import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";

const LANDING_PATH = "/top"; // ✅ landing page
import { supabase } from "../lib/supabaseClient";

const clamp = (n, a, b) => Math.max(a, Math.min(b, n));

const money = (n) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(Number(n || 0));

/* ---------------- STACKED BAR ---------------- */
function StackedBar({ items }) {
  const total = items.reduce((s, x) => s + x.value, 0) || 1;

  return (
    <div>
      <div className="h-4 w-full rounded-full overflow-hidden border border-slate-700 bg-slate-950/40 flex">
        {items.map((it) => (
          <div
            key={it.key}
            className={it.className}
            style={{ width: `${(it.value / total) * 100}%` }}
            title={`${it.label}: ${money(it.value)}`}
          />
        ))}
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {items.map((it) => (
          <div
            key={it.key}
            className="rounded-2xl border border-slate-800 bg-slate-950/40 p-3"
          >
            <div className="text-xs text-slate-300">{it.label}</div>
            <div className="text-lg font-semibold text-slate-50">
              {money(it.value)}
            </div>
            <div className="text-[11px] text-slate-400">{it.hint}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- PROJECTION ---------------- */
function ProjectionLine({ points }) {
  const d = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${p.x * 100} ${100 - p.y * 100}`)
    .join(" ");

  return (
    <svg viewBox="0 0 100 100" className="h-20 w-full mt-3">
      <path d="M0 100 L100 100" stroke="#334155" strokeWidth="1" />
      <path d={d} stroke="#10b981" strokeWidth="3" fill="none" />
    </svg>
  );
}

/* ---------------- MAIN ---------------- */
export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const next = new URLSearchParams(location.search).get("next");

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [monthly, setMonthly] = useState(50);
  const [donatePct, setDonatePct] = useState(50);

  /* ---------------- WEALTH SIM ---------------- */
  const sim = useMemo(() => {
    const m = clamp(monthly, 0, 50000);
    const donate = m * (donatePct / 100);
    const personal = m - donate;

    const insurance = personal * 0.25;
    const savings = personal * 0.2;
    const business = personal * 0.25;
    const assets = personal * 0.3;

    // Registration has been disabled; the simulator no longer needs a plan selector.
    const discountSavings = 0;
    const monthlyImpact =
      donate + insurance + savings + business + assets + discountSavings;

    const yearly = monthlyImpact * 12;

    const points = Array.from({ length: 12 }).map((_, i) => ({
      x: i / 11,
      y: clamp((monthlyImpact * (i + 1)) / yearly, 0, 1),
    }));

    return {
      donate,
      insurance,
      savings,
      business,
      assets,
      discountSavings,
      monthlyImpact,
      yearly,
      points,
    };
  }, [monthly, donatePct]);

  /* ---------------- AUTH HANDLER ---------------- */
  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;

    setLoading(true);
    setErrorMsg("");

    try {
      // ✅ Login only — self-registration has been removed.
      const { error } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password,
      });

      if (error) throw error;

      navigate(next || "/dashboard", { replace: true });
    } catch (err) {
      console.error(err);
      setErrorMsg(err.message || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 px-4 py-10">
      
      
{/* Home button (always visible) */}
<button
  type="button"
  onClick={() => navigate(LANDING_PATH, { replace: true })}
  className="fixed top-4 right-4 z-50 rounded-xl border border-slate-700 bg-slate-900/60 px-4 py-2 text-sm text-slate-100 hover:border-emerald-400 transition-colors"
>
  Home
</button>

<div className="max-w-6xl mx-auto grid gap-6 lg:grid-cols-2">
        {/* AUTH CARD */}
        <motion.div className="rounded-3xl border border-slate-800 bg-slate-900/60 p-6">
          <h1 className="text-2xl font-semibold mb-4">Member Login</h1>

          {errorMsg && (
            <div className="mb-3 rounded-xl bg-red-500/10 border border-red-500/30 p-3 text-sm text-red-300">
              {errorMsg}
            </div>
          )}

          <form onSubmit={submit} className="space-y-3">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              className="w-full rounded-xl bg-slate-950/60 border border-slate-700 px-3 py-2"
            />

            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="w-full rounded-xl bg-slate-950/60 border border-slate-700 px-3 py-2"
            />

            <button
              disabled={loading}
              className="w-full rounded-full bg-emerald-500 py-2 text-slate-950 font-semibold disabled:opacity-60"
            >
              {loading ? "Please wait..." : "Log In"}
            </button>
          </form>
        </motion.div>

        {/* FINANCIAL SIMULATOR */}
        <motion.div className="rounded-3xl border border-slate-800 bg-slate-900/60 p-6">
          <h2 className="text-xl font-semibold mb-2">
            WOW Wealth Engine Snapshot
          </h2>

          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm">
                <span>Monthly amount</span>
                <span className="text-emerald-300">{money(monthly)}</span>
              </div>
              <input
                type="range"
                min={0}
                max={5000}
                step={10}
                value={monthly}
                onChange={(e) => setMonthly(Number(e.target.value))}
                className="w-full"
              />
            </div>

            <div>
              <div className="flex justify-between text-sm">
                <span>Donation percentage</span>
                <span className="text-sky-300">{donatePct}%</span>
              </div>
              <input
                type="range"
                min={0}
                max={100}
                step={5}
                value={donatePct}
                onChange={(e) => setDonatePct(Number(e.target.value))}
                className="w-full"
              />
            </div>

            <StackedBar
              items={[
                { key: "donate", label: "People in Need", value: sim.donate, className: "bg-emerald-500", hint: "Humanitarian aid" },
                { key: "insurance", label: "Protected Savings", value: sim.insurance, className: "bg-sky-500", hint: "Insurance & reserves" },
                { key: "savings", label: "Personal Savings", value: sim.savings, className: "bg-indigo-500", hint: "Savings growth" },
                { key: "business", label: "Business", value: sim.business, className: "bg-amber-500", hint: "Operations" },
                { key: "assets", label: "Assets", value: sim.assets, className: "bg-rose-500", hint: "Ownership" },
              ]}
            />

            <ProjectionLine points={sim.points} />

            <div className="text-sm text-slate-300">
              Estimated monthly total impact:{" "}
              <span className="text-emerald-300 font-semibold">
                {money(sim.monthlyImpact)}
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
